import dayjs, { Dayjs } from 'dayjs';

import { TimelineBooking, TimelineGranularity, TimelineRange } from './timeline.types';
import { timelinePeriodColumnWidth, toDayjs } from './timeline.utils';

const MONTH_AVG_DAYS = 30.4375;
// A slice draws at 75% of the calendar axis's own period pitch: a full week is 90px of rail
// instead of borrowing the whole 120px period column ("make the timeline segments narrower...
// 40% reduction", then "maybe they're too small now, increase by 25%" — user, 2026-08-31; 0.6
// widened back to 0.75). Only to-scale geometry scales — the legibility floors (SLICE_MIN_WIDTH,
// GAP_CAPTION_MIN_WIDTH) keep their tuned values, since they are sized to fit a label rather than
// to measure a span. 90px is also back above the pitch two adjacent full "DD MMM YY" labels need
// (77px, their two half-widths plus LABEL_GAP), which the 40% cut had dipped under.
const SLICE_WIDTH_SCALE = 0.75;
// Must clear a stacked (and ideally a full) label's own extent — see candidatesFor/labelExtent
// below — not just be "narrow but visible": two adjacent one-day booking edges each floor to
// this width, and if it's any narrower than a label's own footprint, neither one can EVER carry
// that form at that pitch — no ladder order or lookahead can rescue a collision that's baked into
// the floor itself. Sized together with LABEL_CHAR_WIDTH/LABEL_PADDING below: those were tightened
// (from 8/8 to 7/4) to model real glyph width more honestly, which on its own shrinks every
// label's footprint; this was widened (from 24, then 40) to give that tighter footprint enough
// pitch to actually land two full-year same-year "DD MMM" labels side by side without colliding —
// the user's own direction ("make the minimum width bigger... decrease the padding") for the exact
// case of a lone one-day booking edge sandwiched between two others. ~3.7 days of visual width for
// a 1-day sliver (the floor bites up to a 3-day slice at the scaled pitch, where it used to bite
// at 2) is the same accepted distortion class as BREAK_COLUMN_WIDTH below (a fixed-width stand-in
// for an arbitrary span, not a to-scale one).
export const SLICE_MIN_WIDTH = 48;
// EDGE stays scaled with the slice pitch (0.75 of its original 72). BREAK no longer follows that
// scale: the pitch-scaled 126px read too thin for the stretch it stands in for, so it draws 50%
// wider ("make the skipped period strips 50% wider" — user, 2026-09-01). Neither is to-scale
// geometry — a break is a fixed stand-in for an arbitrary span — so widening it distorts nothing
// the rail measures; it only costs rail width, which the earlier 168px cut was about. BREAK stays
// clear of GAP_CAPTION_MIN_WIDTH so a compressed stretch still names itself; EDGE is caption-less
// either way.
export const BREAK_COLUMN_WIDTH = 189;
export const EDGE_COLUMN_WIDTH = 54;
// "Skipped periods should be for at least 30 days, i'm seeing a skipped period of just one day"
// (user, 2026-08-29). A short empty gap drawn as a break reads as broken — the 126px band is
// WIDER than just slicing the days — so an empty stretch under this many days renders as
// ordinary weekly slices instead (leading/trailing stretches included; the 54px EDGE break
// obeys the same rule). Measured AFTER adjacent compressible spans coalesce, so a chain of
// small empties that together reach 30 days still compresses as the one stretch the viewer
// actually skips. Sub-threshold gaps are NOT protected — they just aren't worth a break — and
// they now spend slice columns where one break column used to do, so a sparse rail costs more
// columns; MAX_AXIS_COLUMNS still backstops that with the calendar fallback.
export const MIN_BREAK_DAYS = 30;
export const MAX_AXIS_COLUMNS = 160;
// Below this, AxisGapHeader's own duration caption doesn't comfortably fit. Only the leading
// break ever draws this narrow (EDGE_COLUMN_WIDTH, 54) — and it is also the one break with no
// preceding booking to date, so it renders entirely empty.
export const GAP_CAPTION_MIN_WIDTH = 90;

export const slicePxPerDay = (granularity: TimelineGranularity): number =>
  (timelinePeriodColumnWidth(granularity) * SLICE_WIDTH_SCALE) /
  (granularity === 'week' ? 7 : MONTH_AVG_DAYS);

export interface AxisSegment {
  start: Dayjs;
  end: Dayjs;
  x0: number;
  x1: number;
  /**
   * Whole days the segment spans, floored to 1 at construction — the single source of truth for
   * a day count off this segment (e.g. the break band's tooltip), instead of re-deriving it via
   * `end.diff(start, 'day')`.
   */
  days: number;
  startKind: 'anchor' | 'tick';
  isBreak: boolean;
  /**
   * Break segments only: the end date of the booking the gap opens after — the date the header
   * prints at the break's left edge, so a compressed stretch says which period it covers instead
   * of only how long it is ("where we have skipped periods, we don't know what period they
   * cover" — user, 2026-09-01).
   *
   * Deliberately the BOOKING's own end rather than the segment's `start`, which is the coverage
   * boundary and only coincides with it for bars spanning more than a day: `barRun` floors a
   * same-day bar to a full day so `mergeRuns` can't discard it, which pushes that bar's boundary
   * one day past the date its own tooltip shows. Sourcing the date from the bar keeps the axis
   * and the bar telling the same story ("i want the end date from the previous booking" — user).
   *
   * Absent on the LEADING break, whose start is the rail's own origin with no booking before it
   * — the same rule the trailing break already follows in the other direction (a gap's closing
   * date shows only because a booking actually resumes on it).
   */
  precedingBookingEnd?: Dayjs;
  /**
   * Anchor segments only: the real end day of the bar fused to this boundary (see
   * `TimelineBooking.abutsNext`). The bar is drawn through its end day so it meets the next one
   * here, which leaves this line as the only place its end date can be read — the header prints
   * both dates, the ending bar's above the starting bar's, so neither reads a day off.
   */
  junctionEnd?: Dayjs;
}

export interface BookingsAxis {
  segments: AxisSegment[];
  boundaries: Dayjs[];
  totalWidth: number;
  range: TimelineRange;
}

export interface BookingsAxisOptions {
  windowStart?: Dayjs;
  windowEnd?: Dayjs;
  granularity: TimelineGranularity;
  protectedStart?: Dayjs;
  protectedEnd?: Dayjs;
  /**
   * Makes the window a hard display bound instead of a merely-included boundary pair: coverage,
   * operation edges and the protected range are intersected with it, so nothing outside the
   * window can mint a boundary and stretch the rail. A bar trimmed here still draws up to the
   * rail edge with its real dates in the tooltip — offsetForDateOnAxis already clamps
   * out-of-range dates to the axis edges. Default (false) keeps the extend semantics: the rail
   * grows to cover every rendered bar, window edges included among the boundaries.
   */
  clampToWindow?: boolean;
}

interface CoverageRun {
  start: number;
  end: number;
}

const DAY_MS = 86_400_000;

// Availability fillers answer the search and never count — except "Outside availability", which
// says the row itself is not on offer then: a fact to show wherever it falls, like a booking.
const isRendered = (bar: TimelineBooking): boolean =>
  (bar.status !== 'AVAILABLE' && bar.status !== 'UNAVAILABLE') ||
  bar.unavailableStatus === 'OUTSIDE_AVAILABILITY';

const isOperationBar = (bar: TimelineBooking): boolean =>
  !!(
    bar.isExtension ||
    bar.isPendingExtension ||
    bar.isExtensionSegment ||
    bar.isRedecoration ||
    bar.isStagedRedecoration
  );

/**
 * Collapses to local midnight rather than the bar's raw instant. This must match
 * TimelinePeriodCell's own `startOf('day')` bar-cap math, or the axis's boundaries and the bars
 * drawn against it round differently and drift apart in timezones east of UTC. The backend's
 * `T23:59:59Z` day-bounds canon never reaches this read: every assembly point collapses raw
 * instants to the days they mean first (timelineBarStart/timelineBarEnd), so the local midnight
 * here is the meant day's own.
 */
const dayStartMs = (value: TimelineBooking['start']): number =>
  toDayjs(value)!.startOf('day').valueOf();

// Through dayjs rather than `+ DAY_MS`: across a DST change the two differ by an hour, and the
// run must land exactly on the neighbour's own day start to share its boundary.
const dayAfterMs = (value: TimelineBooking['end']): number =>
  toDayjs(value)!.startOf('day').add(1, 'day').valueOf();

/**
 * The {start, end} a bar occupies on the axis, exclusive end. A bar whose start and end collapse
 * to the same local day — a one-day booking — would otherwise produce a zero-length run that
 * `mergeRuns` discards outright, vanishing the booking from both coverage and boundaries; flooring
 * the end to start + one day keeps it on the axis as its own one-day segment.
 *
 * The same floor also catches a bar with its end before its start: it collapses to a one-day run
 * at the bar's own start date rather than a negative-length one `mergeRuns` would likewise discard.
 * Purely defensive — every caller today already filters inverted bars out before they reach here.
 */
const barRun = (bar: TimelineBooking): CoverageRun => {
  const start = dayStartMs(bar.start);
  // A bar that meets the next one runs through its end day, so the two share a boundary.
  const end = bar.abutsNext ? dayAfterMs(bar.end) : dayStartMs(bar.end);
  return { start, end: end > start ? end : start + DAY_MS };
};

/**
 * The key that reunites an operation bar a conflict cut into pieces. `splitBarsAroundConflicts`
 * spreads `...bar` onto every piece, so each piece still carries the operation flag and, where the
 * booking has one, the same `operationIds`. A bar without `operationIds` falls back to its own id
 * with the `-partN` suffix `splitBarsAroundConflicts` mints stripped back off, which reunites the
 * pieces the same way.
 */
const operationGroupKey = (bar: TimelineBooking): string =>
  bar.operationIds && bar.operationIds.length > 0
    ? bar.operationIds.join('|')
    : bar.id.replace(/-part\d+$/, '');

const mergeRuns = (runs: CoverageRun[]): CoverageRun[] => {
  const sorted = runs.filter((r) => r.end > r.start).sort((a, b) => a.start - b.start);
  const merged: CoverageRun[] = [];
  for (const run of sorted) {
    const last = merged[merged.length - 1];
    if (last && run.start <= last.end) last.end = Math.max(last.end, run.end);
    else merged.push({ ...run });
  }
  return merged;
};

/**
 * The segment map a bookings-axis rail draws instead of uniform calendar periods.
 *
 * Boundaries come from per-row coverage runs rather than raw bar edges: the order rail splits an
 * own bar around the conflicts that cut it, and those split edges would otherwise mint header
 * dates for clashes. Merging every rendered bar back into contiguous runs re-fuses the pieces, so
 * a boundary is a date something starts or stops being booked — plus the edges of operation bars
 * (extensions, redecorations, staged periods), which abut the base bar and would vanish into the
 * run, and the rail window itself.
 *
 * An operation bar contributes its group's outer edges, never a piece's own — a conflict cuts an
 * extension or redecoration exactly as it cuts a base bar, and ungrouped pieces would mint the same
 * clash boundaries the coverage-run merge already re-fuses away.
 *
 * Non-compressed spans subdivide at the granularity unit, so a long booking still shows its weekly
 * or monthly rhythm instead of collapsing into one block. A span inside the caller's protected
 * period never compresses either — the period is what the caller searched for, and compressing it
 * would hide free inventory the search exists to surface. Neither does an empty stretch shorter
 * than MIN_BREAK_DAYS — a short gap drawn as a break reads as broken, so it slices like any
 * occupied stretch instead.
 *
 * Returns null when nothing renders, so the caller can fall back to the calendar axis rather than
 * draw a zero-column rail — unless a protected period is set, which draws on its own.
 */
export const buildBookingsAxis = <R>(
  rows: R[],
  getRowBookings: (row: R) => TimelineBooking[],
  options: BookingsAxisOptions,
): BookingsAxis | null => {
  const { windowStart, windowEnd, granularity, protectedStart, protectedEnd, clampToWindow } =
    options;
  const boundarySet = new Set<number>();
  const coverage: CoverageRun[] = [];
  // The day each rendered bar itself ends on — the source for every break's `precedingBookingEnd`
  // (see AxisSegment). Kept apart from `coverage`, whose runs are merged across bars and floored
  // per `barRun`, both of which destroy exactly the per-bar end date this needs.
  const barEndDays: number[] = [];
  // Fused junction boundary → the real end day of the bar that ends there (AxisSegment.junctionEnd).
  const junctionEndByMs = new Map<number, number>();
  // Boundaries where something begins — what a window or protected end fuses forward onto.
  const startBoundaries = new Set<number>();

  // An invalid window bound is dropped rather than propagated as NaN; a valid but inverted pair
  // (end before start) is meaningless as a window, so both are dropped together. Hoisted above
  // the row loop because clamping needs the bounds while runs are still being collected.
  const validStart = windowStart?.isValid() ? windowStart : undefined;
  const validEnd = windowEnd?.isValid() ? windowEnd : undefined;
  const inverted = !!(validStart && validEnd && validEnd.isBefore(validStart));

  const clampStartMs =
    clampToWindow && !inverted && validStart ? validStart.startOf('day').valueOf() : -Infinity;
  const clampEndMs =
    clampToWindow && !inverted && validEnd ? validEnd.startOf('day').valueOf() : Infinity;
  // Identity when not clamping (±Infinity bounds); an emptied run keeps end <= start, which
  // every consumer below already discards.
  const clampRun = ({ start, end }: CoverageRun): CoverageRun => ({
    start: Math.max(start, clampStartMs),
    end: Math.min(end, clampEndMs),
  });

  for (const row of rows) {
    const rendered = (getRowBookings(row) ?? []).filter(isRendered);
    if (!rendered.length) continue;

    const runs = mergeRuns(rendered.map(barRun).map(clampRun));
    coverage.push(...runs);
    runs.forEach((run) => {
      boundarySet.add(run.start);
      boundarySet.add(run.end);
      startBoundaries.add(run.start);
    });

    // A bar fused to its neighbour still marks where that neighbour starts: the merge above folds
    // the two runs into one, but the junction is exactly the date the viewer wants to read — both
    // of them, since the ending bar's own end date has nowhere else left to show.
    rendered
      .filter((bar) => bar.abutsNext)
      .forEach((bar) => {
        const { start, end } = clampRun(barRun(bar));
        if (end <= start) return;
        boundarySet.add(end);
        startBoundaries.add(end);
        junctionEndByMs.set(end, dayStartMs(bar.end));
      });

    // `min` against the bar's own clamped run end serves two cases at once: it undoes barRun's
    // same-day floor (run.end is a day past a one-day bar's real end), and it trims a bar the
    // window cut short to the last day the rail actually shows. A bar clamped out of the rail
    // entirely contributes nothing.
    rendered.forEach((bar) => {
      const run = clampRun(barRun(bar));
      if (run.end <= run.start) return;
      barEndDays.push(Math.min(dayStartMs(bar.end), run.end));
    });

    // Scoped to this row: an operation id is only ever reused within the bookings it was cut
    // from, but nothing stops two unrelated rows from sharing a key (a fallback bar id, or a
    // coincidentally-duplicated `operationIds`). A group shared across rows would fuse their
    // outer edges into one, silently swallowing the boundary in between.
    const operationGroups = new Map<string, CoverageRun>();
    rendered.filter(isOperationBar).forEach((b) => {
      const key = operationGroupKey(b);
      const { start, end } = barRun(b);
      const group = operationGroups.get(key);
      if (group) {
        group.start = Math.min(group.start, start);
        group.end = Math.max(group.end, end);
      } else {
        operationGroups.set(key, { start, end });
      }
    });
    operationGroups.forEach((group) => {
      const { start, end } = clampRun(group);
      if (!(end > start)) return;
      boundarySet.add(start);
      boundarySet.add(end);
      startBoundaries.add(start);
    });
  }

  const validProtectedStart = protectedStart?.isValid() ? protectedStart : undefined;
  const validProtectedEnd = protectedEnd?.isValid() ? protectedEnd : undefined;
  const protectedInverted = !!(
    validProtectedStart &&
    validProtectedEnd &&
    validProtectedEnd.isBefore(validProtectedStart)
  );
  // Either edge alone is a legitimate protected range (open on the other side) — the highlight
  // predicate and callers routinely supply just one, so both are optional independently.
  const hasProtection = !protectedInverted && !!(validProtectedStart || validProtectedEnd);

  // Nothing rendered means nothing to draw — except on a rail that protects the period its caller
  // searched for, which is exactly what that rail exists to show. An add-booking picker whose
  // spaces are all free carries availability fillers alone (never counted as rendered), and still
  // draws its period rather than the calendar fallback.
  if (!boundarySet.size && !hasProtection) return null;

  const windowStartMs = validStart && !inverted ? validStart.startOf('day').valueOf() : undefined;
  let protectedStartMs = hasProtection ? validProtectedStart?.startOf('day').valueOf() : undefined;
  if (windowStartMs !== undefined) startBoundaries.add(windowStartMs);
  if (protectedStartMs !== undefined) startBoundaries.add(Math.max(protectedStartMs, clampStartMs));

  // An end sits on its own last day's midnight, like a bar's. When something begins the next day,
  // the two dates would be one-day-apart lines; the end moves onto that line instead, which then
  // prints both dates stacked (AxisSegment.junctionEnd).
  const fuseEndForward = (endMs: number): number => {
    const next = dayjs(endMs).add(1, 'day').valueOf();
    if (!startBoundaries.has(next)) return endMs;
    if (!junctionEndByMs.has(next)) junctionEndByMs.set(next, endMs);
    return next;
  };

  if (windowStartMs !== undefined) boundarySet.add(windowStartMs);
  if (validEnd && !inverted) boundarySet.add(fuseEndForward(validEnd.startOf('day').valueOf()));

  let protectedEndMs =
    hasProtection && validProtectedEnd
      ? fuseEndForward(validProtectedEnd.startOf('day').valueOf())
      : undefined;

  // A same-day campaign period (start === end) is legal from the Spaces date filter. Without
  // this floor, protectedStartMs === protectedEndMs and every empty span passes the "outside
  // protection" test on one side or the other, so the searched day itself never gets isolated
  // as its own boundary and compresses away with everything around it.
  if (protectedStartMs !== undefined && protectedEndMs !== undefined) {
    protectedEndMs = Math.max(protectedEndMs, protectedStartMs + DAY_MS);
  }

  // Folded INTO the clamped window (identity without clampToWindow): a protected edge outside it
  // would otherwise mint an out-of-window boundary and stretch the rail past the hard bound.
  if (protectedStartMs !== undefined) {
    protectedStartMs = Math.min(Math.max(protectedStartMs, clampStartMs), clampEndMs);
  }
  if (protectedEndMs !== undefined) {
    protectedEndMs = Math.max(Math.min(protectedEndMs, clampEndMs), clampStartMs);
  }

  if (protectedStartMs !== undefined) boundarySet.add(protectedStartMs);
  if (protectedEndMs !== undefined) boundarySet.add(protectedEndMs);

  const bounds = [...boundarySet].sort((a, b) => a - b);
  if (bounds.length < 2) return null;

  const mergedCoverage = mergeRuns(coverage);
  const isCovered = (start: number, end: number) =>
    mergedCoverage.some((run) => run.start < end && run.end > start);

  const rawSpans = bounds.slice(0, -1).map((start, idx) => {
    const end = bounds[idx + 1];
    const empty = !isCovered(start, end);
    const compressible =
      empty &&
      (!hasProtection ||
        end <= (protectedStartMs ?? -Infinity) ||
        start >= (protectedEndMs ?? Infinity));
    return { start, end, compressible };
  });

  // A window edge can land strictly inside a booking gap, splitting one empty stretch into two
  // adjacent empty spans whose shared boundary has nothing either side to label. Coalescing folds
  // any run of consecutive empty spans into one before widths or boundaries are derived from them.
  const spans: typeof rawSpans = [];
  rawSpans.forEach((span) => {
    const last = spans[spans.length - 1];
    if (last && span.compressible && last.compressible) last.end = span.end;
    else spans.push({ ...span });
  });

  // Coalesce first, threshold second — see MIN_BREAK_DAYS. Demoted spans fall through to the
  // ordinary slicing branch below, exactly like an occupied stretch.
  spans.forEach((span) => {
    if (span.compressible && Math.round((span.end - span.start) / DAY_MS) < MIN_BREAK_DAYS) {
      span.compressible = false;
    }
  });

  const unitPx = slicePxPerDay(granularity);
  // Steps from the span's own anchor (`anchor.add(unit * n)`), never from the previous slice's
  // edge: dayjs clamps a month-add to the shorter target month (Jan 31 + 1mo = Feb 29), and
  // compounding that clamp onto the next add would ratchet every later slice down to day 29.
  // Recomputing from the fixed anchor each time keeps every slice's length exact.
  const addUnit = (anchor: Dayjs, n: number): Dayjs =>
    granularity === 'week' ? anchor.add(7 * n, 'day') : anchor.add(n, 'month');

  // The last day anything was booked on at or before `ms` — the booking a gap opens after. Sorted
  // once here rather than searched over the raw array per break: a break's own span start moves
  // strictly rightward down the loop, but a plain scan per break stays clearer than threading a
  // pointer through it, and there are only ever a handful of breaks on a rail.
  const sortedBarEndDays = [...barEndDays].sort((a, b) => a - b);
  const bookingEndAtOrBefore = (ms: number): Dayjs | undefined => {
    let last: number | undefined;
    for (const day of sortedBarEndDays) {
      if (day > ms) break;
      last = day;
    }
    return last === undefined ? undefined : dayjs(last);
  };

  /**
   * The date a break's header names — set only when a booking runs right up to the gap's own
   * opening, which is what makes that date the gap's own edge rather than merely the last booking
   * somewhere to its left.
   *
   * An empty stretch does not always compress: a protected period never does, and one under
   * MIN_BREAK_DAYS is demoted to ordinary slices. Either can sit between the last real booking
   * and a break, and dating the break from that booking would place the gap's start months before
   * it actually begins. Testing the day BEFORE the span covers both bar shapes at once — the
   * coverage run of a multi-day bar ends ON the break's start, a same-day bar's run one day past
   * it (barRun's floor) — and it is also what leaves the LEADING break unlabelled, since nothing
   * precedes the rail's own origin.
   */
  const precedingBookingEnd = (spanStart: number): Dayjs | undefined =>
    isCovered(spanStart - DAY_MS, spanStart) ? bookingEndAtOrBefore(spanStart) : undefined;

  let x = 0;
  const segments: AxisSegment[] = [];
  for (let spanIdx = 0; spanIdx < spans.length; spanIdx += 1) {
    const span = spans[spanIdx];

    if (span.compressible) {
      const days = Math.max(1, Math.round((span.end - span.start) / DAY_MS));
      const width = spanIdx === 0 ? EDGE_COLUMN_WIDTH : BREAK_COLUMN_WIDTH;
      segments.push({
        start: dayjs(span.start),
        end: dayjs(span.end),
        x0: x,
        x1: x + width,
        days,
        startKind: 'anchor',
        isBreak: true,
        precedingBookingEnd: precedingBookingEnd(span.start),
      });
      x += width;
      if (segments.length > MAX_AXIS_COLUMNS) return null;
      continue;
    }

    const anchor = dayjs(span.start);
    let sliceStart = anchor;
    let n = 0;
    while (sliceStart.valueOf() < span.end) {
      n += 1;
      const nextTick = addUnit(anchor, n);
      const sliceEnd = nextTick.valueOf() < span.end ? nextTick : dayjs(span.end);
      const days = Math.max(1, Math.round((sliceEnd.valueOf() - sliceStart.valueOf()) / DAY_MS));
      const width = Math.max(SLICE_MIN_WIDTH, days * unitPx);
      const junctionEnd = n === 1 ? junctionEndByMs.get(span.start) : undefined;
      segments.push({
        start: sliceStart,
        end: sliceEnd,
        x0: x,
        x1: x + width,
        days,
        startKind: n === 1 ? 'anchor' : 'tick',
        isBreak: false,
        ...(junctionEnd === undefined ? {} : { junctionEnd: dayjs(junctionEnd) }),
      });
      x += width;
      // Bails the instant a pathological range (e.g. a bar dated centuries out) crosses the cap,
      // instead of finishing the whole subdivision first and discarding it one line later.
      if (segments.length > MAX_AXIS_COLUMNS) return null;
      sliceStart = sliceEnd;
    }
  }

  if (!segments.length || segments.length > MAX_AXIS_COLUMNS) return null;

  const boundaries = [segments[0].start, ...segments.map((s) => s.end)];
  return {
    segments,
    boundaries,
    totalWidth: x,
    range: {
      start: boundaries[0],
      end: boundaries[boundaries.length - 1],
      periods: boundaries,
    },
  };
};

export const offsetForDateOnAxis = (date: Dayjs, axis: BookingsAxis): number => {
  const t = date.valueOf();
  const { segments, totalWidth } = axis;
  if (t <= segments[0].start.valueOf()) return 0;
  if (t >= segments[segments.length - 1].end.valueOf()) return totalWidth;

  const segment = segments.find((s) => t < s.end.valueOf()) ?? segments[segments.length - 1];
  const fraction =
    (t - segment.start.valueOf()) / (segment.end.valueOf() - segment.start.valueOf());
  return segment.x0 + fraction * (segment.x1 - segment.x0);
};

// The year is redundant noise for the common case (a date within the year the user is already
// looking at) and only earns its keep once a boundary falls outside it. dayjs's locale is synced
// to the UI language app-wide (see InternationalizationProvider), so MMM localizes for free.
export const formatAxisBoundary = (boundary: Dayjs): string =>
  boundary.format(boundary.year() === dayjs().year() ? 'DD MMM' : 'DD MMM YY');

type LabelExtent = [number, number];

// A flat extent assumed every label was the same width; with the date format now length-variable
// ("24 Aug" vs "24 Aug 27"), the extent has to be estimated per label instead. Tightened from an
// original 8px/char + 8px padding: that combination overestimated real glyph width at 14px Neue
// Frutiger World weight 500 by ~25% for our strings (digits + one capital + lowercase month), and
// the overestimate was itself what forced narrow neighbors down the degradation ladder more than
// their actual rendered width required. 7px/char is close to the real average; the remaining 4px
// is a collision margin against per-glyph variance (a wide month like "May" still renders closer
// to 26px than the 25px this models, but the gap is small enough that SLICE_MIN_WIDTH's own
// headroom absorbs it) rather than a from-scratch width estimate.
const LABEL_CHAR_WIDTH = 7;
const LABEL_PADDING = 4;
export const labelExtent = (text: string): number => text.length * LABEL_CHAR_WIDTH + LABEL_PADDING;

const centeredExtent = (center: number, width: number): LabelExtent => [
  center - width / 2,
  center + width / 2,
];

// Chart-axis style: a label centers on its BOUNDARY LINE (x0), not its column's midpoint — the
// column's own width (x1) plays no part any more. The one exception is the rail's own left edge:
// centering a wide label exactly at x0 = 0 (a booking covering the window start, no leading
// break) would bleed half its width left, over the pinned name column, which has nothing to do
// with the axis. Clamping to at least half the label's own width keeps its left edge at (not
// past) the rail's origin — a no-op whenever x0 already clears that (the overwhelming majority
// of labels, including the trailing one: totalWidth is virtually always far larger than any
// label's own half-width). No symmetric right-side clamp exists or is needed: every non-trailing
// label has more rail after it to overhang into, and the trailing label's own column
// (EDGE_COLUMN_WIDTH, 72px) is comfortably wider than any label's half-width.
const centerFor = (x0: number, width: number): number => Math.max(x0, width / 2);

const extentOf = (x0: number, text: string): LabelExtent => {
  const width = labelExtent(text);
  return centeredExtent(centerFor(x0, width), width);
};

// The per-char metrics above model INK — the glyphs themselves — as tightly as real font
// measurements allow, which means a modeled extent can be right at (or, for a wide glyph like
// "M", a hair under) the real rendered width. Two labels the model says "just barely clear" can
// therefore render with zero real air between them, or touching outright: Round 9 already found
// the collision check had no separate margin of its own — raw extents alone arbitrated — so any
// modeled-vs-real delta became a visible collision with nothing to absorb it. LABEL_GAP is that
// absorber: not a wider ink model (the per-char metrics stay tight on purpose — that's what lets
// labels promote up the ladder at all), but a minimum required AIR gap, enforced on top of
// whatever the ink models already compute. One dial, one meaning: space between dates, never
// space within one.
const LABEL_GAP = 10;

// "Too close" now covers genuine geometric overlap AND any real-but-under-LABEL_GAP gap between
// two extents — whichever extent is on which side, exactly one of the two comparisons below is
// the one that matters (the other is trivially true), so the full LABEL_GAP is added to whichever
// side that comparison needs; inflating both extents by LABEL_GAP / 2 apiece instead (a seemingly
// more "symmetric" formulation) reduces to the exact same inequality algebraically — there is no
// behavioral difference, only which side of the arithmetic carries the constant.
const tooClose = (a: LabelExtent, b: LabelExtent): boolean =>
  a[0] < b[1] + LABEL_GAP && a[1] + LABEL_GAP > b[0];

export interface AxisLabel {
  line1: string;
  /** Present only for the stacked rung — line1/line2 render as two centered lines instead of one. */
  line2?: string;
  /** The rail-global x this label centers on — segment.x0, clamped per centerFor above. */
  centerX: number;
  /**
   * Present only on a MERGED label: the segment index of the pair's SECOND anchor — or one past
   * the last segment when that anchor is the trailing boundary column, matching the hook's own
   * field numbering for it. A merged label is hosted in the FIRST anchor's `labels[]` slot while
   * speaking for both boundaries; this names the other one, so the render layer can reach that
   * column's gridline too.
   */
  pairedIdx?: number;
  /**
   * Present only on the cross-month merged form, where line1 and line2 are the pair's two dates
   * one per line: hover targets each line to its own boundary (line1 → the hosting column, line2
   * → pairedIdx) instead of lighting both at once. The dash forms can't split — their one text
   * names both dates — so they mark both columns together and never carry this.
   */
  pairPerLine?: boolean;
}

interface LabelCandidate {
  label: AxisLabel;
  extent: LabelExtent;
}

interface PlacedLabel extends LabelCandidate {
  /**
   * Whether the placement bottomed out on the ladder's last rung, the bare day number. Exposed
   * only internally (never on the public AxisLabel) so resolveAxisLabels can tell "landed rich
   * enough, leave it alone" apart from "a lone number, worth trying to upgrade via a merge"
   * without re-deriving the rung from the label's own shape (which is ambiguous: a bare day and a
   * not-yet-classified label can look alike).
   */
  isDayOnly: boolean;
}

/**
 * The date a segment's header label speaks for. Its own start for every ordinary anchor; for a
 * break, the end of the booking the gap opens after (see AxisSegment.precedingBookingEnd) — a
 * break's own start is the coverage boundary, which reads a day late after barRun's same-day
 * floor and is not the date the bar beside it shows.
 */
const labelDateOf = (segment: AxisSegment): Dayjs =>
  segment.isBreak ? (segment.precedingBookingEnd ?? segment.start) : segment.start;

/** Would `segment`'s own day-only form clear every extent in `extents`? */
const dayOnlyFits = (segment: AxisSegment, extents: LabelExtent[]): boolean => {
  const dayExtent = extentOf(segment.x0, labelDateOf(segment).format('DD'));
  return !extents.some((e) => tooClose(dayExtent, e));
};

/**
 * The label shapes one anchor can take, most to least informative — placeAnchorLabel's ladder
 * walks this array in order:
 * - full: one line, `DD MMM[ YY]` (formatAxisBoundary) — the richest form.
 * - stacked: two lines, the bare day on top and the month(+year, for a non-current year) below —
 *   the same information as full, laid out narrower (its extent is the WIDER of its two lines,
 *   not their sum) at the cost of needing two lines of vertical room instead of one.
 * - day: one line, the bare day number — the floor; loses the month entirely.
 * Every date's three candidates are strictly decreasing in width: stacked's width is always
 * exactly 24px less than full's (both are governed by the same month[+year] text; full just
 * prepends the 2-digit day and a space to it), and day is never wider than stacked (its own line
 * alone vs. the wider of two lines that includes that same day). So once one candidate clears
 * `placed`, every later one in this list trivially does too — including under centerFor's origin
 * clamp: a wider candidate's own clamp threshold (half its width) is always >= a narrower one's,
 * so for any given x0 either both clamp (both extents then start at exactly 0, narrower ending
 * sooner) or the narrower one alone is already past its own threshold — either way the narrower
 * candidate's extent stays a subset of the wider one's, never reaching further in either
 * direction — and, since LABEL_GAP (see tooClose) inflates both sides of every comparison by the
 * same fixed amount regardless of which extent is being tested, a subset relationship between two
 * extents survives that inflation too: if the wider one clears `placed` with the gap enforced,
 * the narrower one — reaching no further in either direction — clears it by at least as much.
 * placeAnchorLabel's lookahead loop below relies on this to skip re-checking candidates against
 * `placed`.
 */
const candidatesFor = (date: Dayjs, x0: number): LabelCandidate[] => {
  const full = formatAxisBoundary(date);
  const day = date.format('DD');
  const month = date.year() === dayjs().year() ? date.format('MMM') : date.format('MMM YY');
  const stackedWidth = Math.max(labelExtent(day), labelExtent(month));
  const fullWidth = labelExtent(full);
  const dayWidth = labelExtent(day);

  const fullCenter = centerFor(x0, fullWidth);
  const stackedCenter = centerFor(x0, stackedWidth);
  const dayCenter = centerFor(x0, dayWidth);

  return [
    {
      label: { line1: full, centerX: fullCenter },
      extent: centeredExtent(fullCenter, fullWidth),
    },
    {
      label: { line1: day, line2: month, centerX: stackedCenter },
      extent: centeredExtent(stackedCenter, stackedWidth),
    },
    {
      label: { line1: day, centerX: dayCenter },
      extent: centeredExtent(dayCenter, dayWidth),
    },
  ];
};

/**
 * One anchor's degradation ladder (see candidatesFor): try its full `DD MMM[ YY]` label; if that
 * extent collides with any extent already placed, retry stacked (day over month); if that too
 * collides, retry the bare day number; if even that collides, give up. Checked against `placed`,
 * which holds what earlier anchors ACTUALLY claimed — itself possibly degraded — never the
 * hypothetical full-width extent a degraded neighbor didn't end up using.
 *
 * `next` (the very next real anchor in left-to-right order, if any) adds one step of lookahead:
 * a label wide enough to place here can still reach far enough to block even `next`'s day-only
 * form — a real, observed case (a booking-start anchor rendering nothing at all, immediately
 * after a neighbor that had no trouble claiming its own full date). Greedily maximizing THIS
 * anchor's own label can cost a neighbor its entire existence, which is never worth it — so when
 * placing the best-fitting candidate here would starve next of even day-only, this anchor walks
 * its remaining, more-degraded candidates (stacked before day-only, so a neighbor's month
 * survives wherever giving up just the full/stacked difference is enough) for the first one that
 * saves next, and falls back to its own best fit only if none of them do. It never looks further
 * than one anchor ahead, and never revisits an anchor already placed to its left.
 */
/**
 * The richest form a fused junction can take (see AxisSegment.junctionEnd): the ending bar's end
 * date stacked over the starting bar's start date, both full. Its extent is the wider of the two,
 * so it is never narrower than the start date's own full form — which keeps candidatesFor's
 * width ordering intact when it is prepended to that ladder: a junction that cannot fit both
 * dates degrades to the start date alone, exactly as the boundary read before it was fused.
 */
const junctionCandidateFor = (junctionEnd: Dayjs, date: Dayjs, x0: number): LabelCandidate => {
  const line1 = formatAxisBoundary(junctionEnd);
  const line2 = formatAxisBoundary(date);
  const width = Math.max(labelExtent(line1), labelExtent(line2));
  const center = centerFor(x0, width);
  return { label: { line1, line2, centerX: center }, extent: centeredExtent(center, width) };
};

const placeAnchorLabel = (
  date: Dayjs,
  x0: number,
  placed: LabelExtent[],
  next?: AxisSegment,
  junctionEnd?: Dayjs,
): PlacedLabel | undefined => {
  const candidates = junctionEnd
    ? [junctionCandidateFor(junctionEnd, date, x0), ...candidatesFor(date, x0)]
    : candidatesFor(date, x0);
  const lastRung = candidates.length - 1;
  const fittingIndex = candidates.findIndex((c) => !placed.some((p) => tooClose(c.extent, p)));
  if (fittingIndex === -1) return undefined;

  const best = candidates[fittingIndex];
  if (next && !dayOnlyFits(next, [...placed, best.extent])) {
    // `best` would starve next of even its own day-only form. The remaining candidates are each
    // already proven to clear `placed` (see candidatesFor's width ordering), so this only needs
    // to find the first one that also leaves next room for at least day-only.
    for (let i = fittingIndex + 1; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      if (dayOnlyFits(next, [...placed, candidate.extent])) {
        return { ...candidate, isDayOnly: i === lastRung };
      }
    }
  }
  return { ...best, isDayOnly: fittingIndex === lastRung };
};

// Type guard, not just a boolean: narrows `placed` to PlacedLabel wherever it's true, so callers
// never need a non-null assertion to read `.label`/`.extent` off the rich branch. True once an
// anchor's own best candidate — after the ladder above, lookahead included — landed above the
// bare-day rung (a junction's two dates, full, or stacked), rich enough on its own that it is
// never a merge candidate, no matter how close its neighbor sits. False covers both "landed on
// bare day" and "didn't land at all" (every candidate collided) — both read as "a lone number,
// or nothing" to a viewer, which is exactly the trigger resolveAxisLabels uses below to attempt
// a merge.
const isRichPlacement = (placed: PlacedLabel | undefined): placed is PlacedLabel =>
  !!placed && !placed.isDayOnly;

// Sanity cap on how far apart two anchors can sit and still share one label — beyond this a
// merged pair would visibly float away from the two lines it's meant to caption, reading as
// wrong rather than helpful. Unrelated to LABEL_GAP (that's about air between two SEPARATE
// labels; this is about how far one shared label may drift from the boundaries it speaks for).
const MERGE_MAX_PITCH = 96;

interface MergedCandidate {
  label: AxisLabel;
  extent: LabelExtent;
}

/**
 * A merged pair's own mini-ladder, richest first — mirrors candidatesFor's shape (an ordered list,
 * width-decreasing) but for two dates sharing one label instead of one date alone. Two forms only:
 *
 * - Same month (and year): one line, `DD–DD MMM[ YY]` (en dash) — reads as a single date range.
 *   Degrades to stacked `DD–DD` over `MMM[ YY]` when the one-line form doesn't fit.
 * - Different month or year: stacked two-line, each line a FULL date (`formatAxisBoundary`, so
 *   each gets its own current-year-conditional `[ YY]`) — two whole dates read cleaner stacked
 *   than crammed into one dashed line, and there is no shared month to dash together.
 *
 * Centers on the pair's own MIDPOINT (`(x0A + x0B) / 2`), reusing centerFor's origin clamp so a
 * pair straddling the rail's own start still can't bleed left over the name column, exactly like
 * a solo label.
 */
const mergeCandidatesFor = (
  dateA: Dayjs,
  x0A: number,
  dateB: Dayjs,
  x0B: number,
): MergedCandidate[] => {
  const midCenter = (x0A + x0B) / 2;
  const dayA = dateA.format('DD');
  const dayB = dateB.format('DD');

  if (dateA.isSame(dateB, 'month')) {
    const monthText =
      dateA.year() === dayjs().year() ? dateA.format('MMM') : dateA.format('MMM YY');
    const full = `${dayA}–${dayB} ${monthText}`;
    const stackedLine1 = `${dayA}–${dayB}`;
    const fullWidth = labelExtent(full);
    const stackedWidth = Math.max(labelExtent(stackedLine1), labelExtent(monthText));
    const fullCenter = centerFor(midCenter, fullWidth);
    const stackedCenter = centerFor(midCenter, stackedWidth);
    return [
      {
        label: { line1: full, centerX: fullCenter },
        extent: centeredExtent(fullCenter, fullWidth),
      },
      {
        label: { line1: stackedLine1, line2: monthText, centerX: stackedCenter },
        extent: centeredExtent(stackedCenter, stackedWidth),
      },
    ];
  }

  const lineA = formatAxisBoundary(dateA);
  const lineB = formatAxisBoundary(dateB);
  const width = Math.max(labelExtent(lineA), labelExtent(lineB));
  const center = centerFor(midCenter, width);
  return [
    {
      label: { line1: lineA, line2: lineB, centerX: center, pairPerLine: true },
      extent: centeredExtent(center, width),
    },
  ];
};

/**
 * The first of mergeCandidatesFor's rungs that clears every extent already placed BEFORE the
 * pair (never anything to the pair's right — nothing there has been decided yet; the anchor that
 * follows a merged pair reacts to it via its own normal ladder, the same as it would react to any
 * other already-placed anchor). Returns undefined when even the narrowest merged form still
 * collides — merging never forces a placement neither date could otherwise get near; it only
 * upgrades two anchors that both independently agreed (by way of the trigger check below) they'd
 * rather share a rich label than one keep a lone bare number.
 */
const mergePair = (
  dateA: Dayjs,
  x0A: number,
  dateB: Dayjs,
  x0B: number,
  before: LabelExtent[],
): MergedCandidate | undefined =>
  mergeCandidatesFor(dateA, x0A, dateB, x0B).find(
    (c) => !before.some((p) => tooClose(c.extent, p)),
  );

// One entry per COMMITTED label, solo or merged — never one per anchor index. A merged pair
// collapses its two anchors into a single commitment (isMerged: true, chain-blocked from ever
// being pulled into a further merge), so the next anchor's own placement — and the next merge
// eligibility check — always reacts to what's actually rendering, not a phantom per-anchor slot.
interface Commitment {
  extent: LabelExtent;
  date: Dayjs;
  x0: number;
  anchorIdx: number;
  isMerged: boolean;
}

/**
 * The trailing boundary column's own position and date — see resolveAxisLabels. `x1` is not read
 * by the label math (boundary-centering only needs `x0` — see centerFor), but stays part of the
 * shape: it is still where the trailing column itself actually ends, useful to callers/tests
 * independent of label placement.
 */
export interface TrailingAxisAnchor {
  x0: number;
  x1: number;
  date: Dayjs;
}

export interface ResolvedAxisLabels {
  /** One entry per input segment. */
  labels: (AxisLabel | undefined)[];
  /** The trailing boundary column's own resolved label, if a `trailingAnchor` was given. */
  trailingLabel: AxisLabel | undefined;
}

/**
 * v5 anchors-only label resolution. Only booking/period/window edges (`startKind: 'anchor'`,
 * non-break) ever get a label — the weekly/monthly rhythm (`startKind: 'tick'`) renders nothing
 * at all, at any width or position. (Since Round 19 the header carries no edge ticks for ANY
 * boundary; an anchor's vertical line is the row gridline alone, scoped off `startKind` via the
 * hook's cell class.) Break segments always resolve to `undefined` too — a gap's only text is
 * its own duration caption, handled separately by AxisGapHeader.
 *
 * Anchors are placed left to right via `placeAnchorLabel`'s degradation ladder (full label, then
 * stacked, then day-only, then drop) plus one step of lookahead: an anchor whose own best-fitting
 * candidate would otherwise place still checks whether that placement would leave the very next
 * anchor unable to fit even its day-only form, and walks its own remaining candidates for the
 * first one that saves the neighbor — a later anchor that collides with an already-placed one
 * degrades or drops instead of ever displacing the earlier one, so this is the one case where an
 * anchor's own placement is chosen with a neighbor in mind. Either way, only a label is ever at
 * stake, never an anchor's row gridline (the hook keys the cell's gridline class off `startKind`,
 * not off whether a label survived). `trailingAnchor` — the always-rendered trailing boundary
 * column's own position and date — goes through the identical ladder (no lookahead of its own:
 * nothing follows it), seeded with every real anchor's placed extent, but only after all of
 * them: being the rail's rightmost edge, it can only ever degrade itself, never an earlier anchor
 * (unlike v5's first cut, which rendered it unconditionally). It is skipped entirely when the
 * rail's last segment is a break — see `endsInBreak` below.
 *
 * v6 (user feedback: "it looks strange with just one like 3, can we put it and its neighbor too
 * if it's close?") layers a MERGE step on top: whenever an anchor's ladder above lands it on bare
 * day or drops it entirely (`!isRichPlacement`), before accepting that this anchor tries to pair
 * up with the commitment immediately to its left — its own best candidate landing that low is
 * exactly what a lone, orphaned-looking number means to a viewer, merge-eligible or not. A merge
 * always looks backward, never forward: the anchor doing the looking is always the one being
 * resolved right now, and the only thing that has a committed extent to actually merge WITH is
 * whatever the walk already placed before it — the anchor after a pair hasn't been visited yet,
 * so there is nothing there yet to merge with (it gets its own turn next, reacting to the pair's
 * extent through the exact same ladder as any other already-placed neighbor). This one direction
 * covers every case that matters here without ever revisiting more than one step back: a squeeze
 * caused by direct collision with the preceding anchor (the ordinary case) and a squeeze caused by
 * that anchor's OWN earlier lookahead self-degrading to protect this one (the pair is still
 * exactly these two dates either way) both show up identically as "this anchor landed low," and
 * the trailing boundary — which never has a lookahead or a `next` of its own — is just the last
 * stop of the same walk, so it merges with the last real anchor by the identical rule (`idx`
 * omitted for it since it has no `labels[]` slot of its own to blank out).
 *
 * A merge is eligible only when the previous commitment is (a) not already itself a merged pair —
 * chains of 3+ close anchors pair up greedily left to right, never sharing one label across three
 * dates — and (b) within MERGE_MAX_PITCH. Eligible or not, `mergePair` (mergeCandidatesFor's own
 * richest-first ladder, checked against every extent placed before the PREVIOUS commitment — the
 * pair's own outer neighbor, never anything to its right, which hasn't been decided yet) still has
 * to actually clear that outer neighbor; if even its narrowest form doesn't, the merge attempt is
 * abandoned with no side effects and both anchors fall back to exactly what v6's ladder alone
 * would have given them — merging only ever upgrades a placement, never forces one neither date
 * could otherwise get near.
 *
 * Every returned label centers on its own boundary LINE (`AxisLabel.centerX`, chart-axis style) —
 * a solo label on its x0, a merged pair on its own two boundaries' midpoint — not any column's
 * midpoint; see centerFor/candidatesFor/mergeCandidatesFor. Render (AxisBoundaryHeader, via the
 * hook) is expected to position each label at `centerX` in the rail's own coordinate space; this
 * module only computes where that is, never how to paint it. The one thing render needs to know
 * beyond `{line1, line2?, centerX}` is that a merged label speaks for TWO boundaries while
 * living in one column's slot — `pairedIdx` names the second one, and `pairPerLine` says whether
 * each line owns one of them (see AxisLabel) — so the hover emphasis can reach the right
 * gridline(s).
 */
/*
 * A fused junction (AxisSegment.junctionEnd) leads its ladder with one extra rung: the ending
 * bar's end date stacked over the starting bar's start date. It carries no pairedIdx — both lines
 * speak for the one boundary they sit on — and it degrades into the ordinary single-date ladder
 * for the start date when even that stacked pair cannot fit.
 */
export const resolveAxisLabels = (
  segments: AxisSegment[],
  trailingAnchor?: TrailingAxisAnchor,
): ResolvedAxisLabels => {
  const labels: (AxisLabel | undefined)[] = new Array(segments.length).fill(undefined);
  const commitments: Commitment[] = [];

  // Attempts to fold (date, x0) into the previous commitment as a merged pair. `idx` is that
  // slot's own index in `labels` (omitted for the trailing anchor, which has none). Returns
  // whether the merge landed; a `false` return is always a total no-op — every mutation below
  // happens only once `mergePair` has already confirmed a clearing candidate exists.
  const tryMergeWithPrevious = (date: Dayjs, x0: number, idx: number | undefined): boolean => {
    const prev = commitments[commitments.length - 1];
    if (!prev || prev.isMerged || x0 - prev.x0 > MERGE_MAX_PITCH) return false;

    const before = commitments.slice(0, -1).map((c) => c.extent);
    const merged = mergePair(prev.date, prev.x0, date, x0, before);
    if (!merged) return false;

    commitments[commitments.length - 1] = {
      extent: merged.extent,
      date: prev.date,
      x0: prev.x0,
      anchorIdx: prev.anchorIdx,
      isMerged: true,
    };
    labels[prev.anchorIdx] = { ...merged.label, pairedIdx: idx ?? segments.length };
    if (idx !== undefined) labels[idx] = undefined;
    return true;
  };

  // Indices only (not the segments themselves) — the lookahead in placeAnchorLabel needs the
  // very next LABELLED boundary, skipping over any ticks in between, which never place a label
  // and so can never be the thing a full label would starve.
  //
  // A break belongs in this walk exactly when it knows the booking it opens after: that date is
  // a real boundary a viewer needs, and routing it through the same ladder as every other anchor
  // is what keeps it from colliding — a short booking pressed against a gap merges its start and
  // end into one `DD–DD MMM` label instead of overprinting. The leading break, which has no
  // booking before it, stays unlabelled and stays out of the walk.
  const anchorIndices = segments
    .map((_, idx) => idx)
    .filter((idx) => {
      const segment = segments[idx];
      if (segment.startKind !== 'anchor') return false;
      return segment.isBreak ? segment.precedingBookingEnd != null : true;
    });

  anchorIndices.forEach((idx, i) => {
    const segment = segments[idx];
    const nextIdx = anchorIndices[i + 1];
    const next = nextIdx == null ? undefined : segments[nextIdx];
    const extentsSoFar = commitments.map((c) => c.extent);
    const labelDate = labelDateOf(segment);
    const placed = placeAnchorLabel(labelDate, segment.x0, extentsSoFar, next, segment.junctionEnd);

    if (isRichPlacement(placed)) {
      commitments.push({
        extent: placed.extent,
        date: labelDate,
        x0: segment.x0,
        anchorIdx: idx,
        isMerged: false,
      });
      labels[idx] = placed.label;
      return;
    }

    if (tryMergeWithPrevious(labelDate, segment.x0, idx)) return;

    // Merge wasn't eligible, or its own outer neighbor blocked it — fall back to this anchor's
    // already-computed placement (bare day if that cleared, dropped otherwise), unchanged from v6.
    if (placed) {
      commitments.push({
        extent: placed.extent,
        date: labelDate,
        x0: segment.x0,
        anchorIdx: idx,
        isMerged: false,
      });
      labels[idx] = placed.label;
    }
  });

  // A rail whose last segment is a break has nothing resuming after it: its trailing boundary is
  // just the far edge of the skipped stretch — the window's own end, not a date anything happens
  // on — and printing it there reads as a booking edge that isn't one ("if at the end of the
  // timeline we have a skipped period because there are no more bookings don't put the date at
  // the end, looks bad" — user, 2026-08-31). It is the same rule every other gap boundary already
  // follows: a gap's opening date is never shown, and an interior gap's closing date is shown
  // only because a booking actually resumes on it. Suppressed BEFORE the ladder rather than
  // after it, since running the ladder could still merge this date backward into the last real
  // anchor's label — printing the very date being suppressed, and dragging that anchor's own
  // label off its boundary to a pair midpoint to do it.
  const endsInBreak = !!segments[segments.length - 1]?.isBreak;

  let trailingLabel: AxisLabel | undefined;
  if (trailingAnchor && !endsInBreak) {
    const extentsSoFar = commitments.map((c) => c.extent);
    const placed = placeAnchorLabel(trailingAnchor.date, trailingAnchor.x0, extentsSoFar);
    if (isRichPlacement(placed)) {
      trailingLabel = placed.label;
    } else if (tryMergeWithPrevious(trailingAnchor.date, trailingAnchor.x0, undefined)) {
      trailingLabel = undefined;
    } else {
      trailingLabel = placed?.label;
    }
  }

  return { labels, trailingLabel };
};

// A rounder constant than MONTH_AVG_DAYS on purpose: this feeds a human-facing "~N months" count,
// not pixel math, and a round 30 reads better than a decimal average would.
const GAP_DAYS_PER_MONTH = 30;
// Below two months, exact days read better than a rounded month count.
const GAP_MONTHS_THRESHOLD_DAYS = 60;

export interface AxisGapDuration {
  unit: 'days' | 'months';
  count: number;
}

/**
 * The day/month split every compressed-span duration display shares — the break cell's tooltip
 * and the gap header's caption say the same skipped stretch in different words, and both must
 * agree on when a count of days is better said as "a couple of months".
 */
export const axisGapDuration = (days: number): AxisGapDuration =>
  days >= GAP_MONTHS_THRESHOLD_DAYS
    ? { unit: 'months', count: Math.round(days / GAP_DAYS_PER_MONTH) }
    : { unit: 'days', count: days };
